(() => {
  const ROOT_SELECTOR = '[data-iflq-root]';
  const CONFIG_SELECTOR = '[data-iflq-config]';
  const APP_SELECTOR = '[data-iflq-app]';

  const TRACKING_PARAMS = new Set([
    'fbclid',
    'gclid',
    'ttclid',
    'msclkid',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id'
  ]);

  function safeParse(value, fallback = null) {
    try {
      return JSON.parse(value);
    } catch (_) {
      return fallback;
    }
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function createSessionId() {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }

    return `ifl_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }

  function initQuiz(root) {
    if (!root || root.dataset.iflqInitialized === 'true') {
      return;
    }

    root.dataset.iflqInitialized = 'true';

    const configNode = root.querySelector(CONFIG_SELECTOR);
    const app = root.querySelector(APP_SELECTOR);

    if (!configNode || !app) {
      return;
    }

    const config = safeParse(configNode.textContent);

    if (!config) {
      app.innerHTML =
        '<div class="iflq-error">Le quiz ne peut pas être chargé.</div>';
      return;
    }

    if (config.hideThemeChrome) {
      document.body.classList.add('iflq-page');
    }

    const quizSessionId = createSessionId();

    const questionScreens = config.screens.filter((screen) =>
      ['single', 'multi', 'slider'].includes(screen.kind)
    );

    const questionOrdinal = new Map(
      questionScreens.map((screen, index) => [
        screen.id,
        index + 1
      ])
    );

    const state = {
      phase: 'screen',
      screenIndex: 0,
      answers: {},
      answerLabels: {},
      resultId: null,
      startedAt: null,
      completedAt: null
    };

    let lastViewedScreenId = null;
    let completionDataSent = false;

    render();
    emit('QuizView');

    function ensureStarted() {
      if (state.startedAt) {
        return;
      }

      state.startedAt = new Date().toISOString();
      emit('QuizStart');
    }

    function emit(name, payload = {}) {
      const params = {
        quiz_id: config.quizId,
        quiz_version: config.version,
        quiz_session_id: quizSessionId,
        ...payload
      };

      const critical = [
        'QuizStart',
        'QuizComplete',
        'QuizPDPClick'
      ].includes(name);

      const send = () => {
        window.dataLayer = window.dataLayer || [];

        window.dataLayer.push({
          event: `fp_${name}`,
          ...params
        });

        window.dispatchEvent(
          new CustomEvent('fp:quiz', {
            detail: {
              name,
              ...params
            }
          })
        );
      };

      if (critical) {
        send();

        if (typeof window.fbq === 'function') {
          try {
            window.fbq(
              'trackCustom',
              name,
              params
            );
          } catch (_) {}
        }

        return;
      }

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(
          send,
          {
            timeout: 1200
          }
        );
      } else {
        window.setTimeout(send, 0);
      }
    }

    function render() {
      if (state.phase === 'processing') {
        renderProcessing();
        return;
      }

      if (state.phase === 'result') {
        renderResult();
        return;
      }

      renderScreen();
    }

    function renderScreen() {
      const screen =
        config.screens[state.screenIndex];

      if (!screen) {
        startProcessing();
        return;
      }

      const currentQuestion =
        questionOrdinal.get(screen.id) || null;

      const completedQuestions =
        countCompletedQuestionsBefore(
          state.screenIndex
        );

      const progress = Math.max(
        0,
        Math.min(
          100,
          (completedQuestions /
            questionScreens.length) *
            100
        )
      );

      const firstQuestion =
        state.screenIndex === 0 &&
        screen.id === 'q01_length';

      app.innerHTML = `
        <div class="iflq-shell">

          ${
            firstQuestion
              ? `
                <div class="iflq-direct-hook">

                  <h1
                    class="iflq-direct-hook__headline"
                  >
                    ${escapeHtml(
                      config.entry.headline
                    )}
                  </h1>

                  <p
                    class="iflq-direct-hook__promise"
                  >
                    ${escapeHtml(
                      config.entry.promise
                    )}
                  </p>

                </div>
              `
              : ''
          }

          <div class="iflq-topbar">

            ${
              firstQuestion
                ? `
                  <span
                    class="iflq-back iflq-back--empty"
                    aria-hidden="true"
                  ></span>
                `
                : `
                  <button
                    type="button"
                    class="iflq-back"
                    data-action="back"
                  >
                    ${escapeHtml(
                      config.ui.back
                    )}
                  </button>
                `
            }

            <div
              class="iflq-progress"
              aria-label="Progression"
            >
              <span
                style="width:${progress}%"
              ></span>
            </div>

            <div class="iflq-counter">
              ${
                currentQuestion
                  ? `${escapeHtml(
                      config.ui.questionLabel
                    )} ${currentQuestion}/${questionScreens.length}`
                  : ''
              }
            </div>

          </div>

          <div
            class="iflq-panel"
            data-screen-id="${escapeHtml(
              screen.id
            )}"
          >
            ${screenHtml(screen)}
          </div>

        </div>
      `;

      bindScreen(screen);

      if (lastViewedScreenId !== screen.id) {
        emit(
          screen.kind === 'mid_card'
            ? 'QuizCardView'
            : 'QuizQuestionView',
          {
            screen_id: screen.id,
            screen_kind: screen.kind,
            question_number: currentQuestion
          }
        );

        lastViewedScreenId = screen.id;
      }
    }

    function screenHtml(screen) {
      if (screen.kind === 'mid_card') {
        return `
          <div class="iflq-mid">

            <p class="iflq-kicker">
              ${escapeHtml(
                screen.eyebrow
              )}
            </p>

            <h2 class="iflq-question">
              ${escapeHtml(
                screen.headline
              )}
            </h2>

            <p class="iflq-copy">
              ${escapeHtml(
                screen.body
              )}
            </p>

            <div class="iflq-mid-note">
              ${escapeHtml(
                getMidCardObservation()
              )}
            </div>

            <div
              class="iflq-actions iflq-actions--centered"
            >
              <button
                type="button"
                class="iflq-btn"
                data-action="continue"
              >
                ${escapeHtml(
                  screen.cta ||
                  config.ui.continue
                )}
              </button>
            </div>

          </div>
        `;
      }

      if (screen.kind === 'slider') {
        const current =
          state.answers[screen.id] ??
          String(screen.default);

        return `
          <h2 class="iflq-question">
            ${escapeHtml(
              screen.question
            )}
          </h2>

          <div class="iflq-slider-wrap">

            <input
              class="iflq-slider"
              type="range"
              min="${Number(screen.min)}"
              max="${Number(screen.max)}"
              step="${Number(
                screen.step || 1
              )}"
              value="${Number(current)}"
              data-slider
              aria-label="${escapeHtml(
                screen.question
              )}"
            >

            <div
              class="iflq-slider-value"
              data-slider-value
            >
              ${escapeHtml(current)}
            </div>

            <div class="iflq-slider-labels">

              <span>
                ${escapeHtml(
                  screen.minLabel
                )}
              </span>

              <span>
                ${escapeHtml(
                  screen.maxLabel
                )}
              </span>

            </div>

          </div>

          <div class="iflq-actions">

            <button
              type="button"
              class="iflq-btn"
              data-action="slider-next"
            >
              ${escapeHtml(
                screen.cta ||
                config.ui.continue
              )}
            </button>

          </div>
        `;
      }

      const selected =
        screen.kind === 'multi'
          ? normalizeArray(
              state.answers[screen.id]
            )
          : state.answers[screen.id];

      return `
        <h2 class="iflq-question">
          ${escapeHtml(screen.question)}
        </h2>

        ${
          screen.helper
            ? `
              <p class="iflq-helper">
                ${escapeHtml(
                  screen.helper
                )}
              </p>
            `
            : ''
        }

        <div class="iflq-answers">

          ${screen.answers
            .map((answer) => {
              const isSelected =
                screen.kind === 'multi'
                  ? selected.includes(
                      answer.id
                    )
                  : selected ===
                    answer.id;

              return `
                <button
                  type="button"
                  class="iflq-answer"
                  data-answer-id="${escapeHtml(
                    answer.id
                  )}"
                  aria-pressed="${
                    isSelected
                      ? 'true'
                      : 'false'
                  }"
                >
                  ${escapeHtml(
                    answer.label
                  )}
                </button>
              `;
            })
            .join('')}

        </div>

        ${
          screen.kind === 'multi'
            ? `
              <div
                class="iflq-inline-error"
                data-inline-error
                aria-live="polite"
              ></div>

              <div class="iflq-actions">

                <button
                  type="button"
                  class="iflq-btn"
                  data-action="multi-next"
                >
                  ${escapeHtml(
                    screen.cta ||
                    config.ui.continue
                  )}
                </button>

              </div>
            `
            : ''
        }
      `;
    }

    function bindScreen(screen) {
      app
        .querySelector(
          '[data-action="back"]'
        )
        ?.addEventListener(
          'click',
          goBack
        );

      if (screen.kind === 'mid_card') {
        app
          .querySelector(
            '[data-action="continue"]'
          )
          ?.addEventListener(
            'click',
            nextScreen
          );

        return;
      }

      if (screen.kind === 'slider') {
        const input =
          app.querySelector(
            '[data-slider]'
          );

        const output =
          app.querySelector(
            '[data-slider-value]'
          );

        input?.addEventListener(
          'input',
          () => {
            if (output) {
              output.textContent =
                input.value;
            }
          }
        );

        app
          .querySelector(
            '[data-action="slider-next"]'
          )
          ?.addEventListener(
            'click',
            () => {
              if (!input) {
                return;
              }

              ensureStarted();

              recordAnswer(
                screen,
                String(input.value),
                String(input.value)
              );

              nextScreen();
            }
          );

        return;
      }

      if (screen.kind === 'multi') {
        app
          .querySelectorAll(
            '[data-answer-id]'
          )
          .forEach((button) => {
            button.addEventListener(
              'click',
              () => {
                toggleMultiAnswer(
                  screen,
                  button
                );
              }
            );
          });

        app
          .querySelector(
            '[data-action="multi-next"]'
          )
          ?.addEventListener(
            'click',
            () => {
              const selected =
                normalizeArray(
                  state.answers[
                    screen.id
                  ]
                );

              if (!selected.length) {
                setInlineError(
                  config.ui
                    .selectAtLeastOne
                );

                return;
              }

              emit('QuizAnswer', {
                screen_id:
                  screen.id,

                answer_ids:
                  selected,

                answer_labels:
                  selected.map(
                    (id) =>
                      getAnswerLabel(
                        screen,
                        id
                      )
                  )
              });

              nextScreen();
            }
          );

        updateMultiDisabledState(
          screen
        );

        return;
      }

      app
        .querySelectorAll(
          '[data-answer-id]'
        )
        .forEach((button) => {
          button.addEventListener(
            'click',
            () => {
              const answerId =
                button.getAttribute(
                  'data-answer-id'
                );

              const answer =
                screen.answers.find(
                  (item) =>
                    item.id ===
                    answerId
                );

              if (!answer) {
                return;
              }

              ensureStarted();

              recordAnswer(
                screen,
                answer.id,
                answer.label
              );

              nextScreen();
            }
          );
        });
    }

    function toggleMultiAnswer(
      screen,
      button
    ) {
      ensureStarted();

      const answerId =
        button.getAttribute(
          'data-answer-id'
        );

      const answer =
        screen.answers.find(
          (item) =>
            item.id ===
            answerId
        );

      if (!answer) {
        return;
      }

      let selected =
        normalizeArray(
          state.answers[
            screen.id
          ]
        );

      const alreadySelected =
        selected.includes(
          answerId
        );

      if (alreadySelected) {
        selected =
          selected.filter(
            (id) =>
              id !== answerId
          );
      } else if (answer.exclusive) {
        selected = [answerId];
      } else {
        const exclusiveIds =
          new Set(
            screen.answers
              .filter(
                (item) =>
                  item.exclusive
              )
              .map(
                (item) =>
                  item.id
              )
          );

        selected =
          selected.filter(
            (id) =>
              !exclusiveIds.has(id)
          );

        if (
          screen.maxSelections &&
          selected.length >=
            Number(
              screen.maxSelections
            )
        ) {
          setInlineError(
            config.ui.maxReached
          );

          return;
        }

        selected.push(answerId);
      }

      state.answers[
        screen.id
      ] = selected;

      state.answerLabels[
        screen.id
      ] =
        selected.map((id) =>
          getAnswerLabel(
            screen,
            id
          )
        );

      state.resultId = null;

      setInlineError('');
      refreshMultiButtons(
        screen
      );
    }

    function refreshMultiButtons(
      screen
    ) {
      const selected =
        normalizeArray(
          state.answers[
            screen.id
          ]
        );

      app
        .querySelectorAll(
          '[data-answer-id]'
        )
        .forEach((button) => {
          const id =
            button.getAttribute(
              'data-answer-id'
            );

          button.setAttribute(
            'aria-pressed',
            selected.includes(id)
              ? 'true'
              : 'false'
          );
        });

      updateMultiDisabledState(
        screen
      );
    }

    function updateMultiDisabledState(
      screen
    ) {
      if (!screen.maxSelections) {
        return;
      }

      const selected =
        normalizeArray(
          state.answers[
            screen.id
          ]
        );

      const atMax =
        selected.length >=
        Number(
          screen.maxSelections
        );

      app
        .querySelectorAll(
          '[data-answer-id]'
        )
        .forEach((button) => {
          const id =
            button.getAttribute(
              'data-answer-id'
            );

          button.disabled =
            atMax &&
            !selected.includes(id);
        });
    }

    function setInlineError(
      message
    ) {
      const node =
        app.querySelector(
          '[data-inline-error]'
        );

      if (node) {
        node.textContent =
          message || '';
      }
    }

    function recordAnswer(
      screen,
      value,
      label
    ) {
      state.answers[
        screen.id
      ] = value;

      state.answerLabels[
        screen.id
      ] = label;

      state.resultId = null;

      emit('QuizAnswer', {
        screen_id:
          screen.id,

        answer_id:
          value,

        answer_label:
          label
      });
    }

    function nextScreen() {
      state.screenIndex += 1;

      if (
        state.screenIndex >=
        config.screens.length
      ) {
        startProcessing();
        return;
      }

      state.phase = 'screen';
      render();
    }

    function goBack() {
      if (
        state.screenIndex <= 0
      ) {
        return;
      }

      state.screenIndex -= 1;
      state.phase = 'screen';
      render();
    }

    function startProcessing() {
      state.phase = 'processing';
      render();
    }

    function renderProcessing() {
      const messages =
        config.processing
          .messages || [];

      app.innerHTML = `
        <div class="iflq-shell">

          <div
            class="iflq-panel iflq-processing"
          >

            <p class="iflq-kicker">
              ${escapeHtml(
                config.processing
                  .title
              )}
            </p>

            <div
              class="iflq-processing__number"
              data-processing-number
            >
              0%
            </div>

            <div
              class="iflq-processing__message"
              data-processing-message
            >
              ${escapeHtml(
                messages[0] || ''
              )}
            </div>

            <div
              class="iflq-processing__track"
              aria-hidden="true"
            >
              <span
                class="iflq-processing__bar"
                data-processing-bar
              ></span>
            </div>

          </div>

        </div>
      `;

      const totalMs =
        Number(
          config.processingMs ||
          1250
        );

      const number =
        app.querySelector(
          '[data-processing-number]'
        );

      const bar =
        app.querySelector(
          '[data-processing-bar]'
        );

      const message =
        app.querySelector(
          '[data-processing-message]'
        );

      const start =
        performance.now();

      function frame(now) {
        const ratio =
          Math.min(
            1,
            (now - start) /
              totalMs
          );

        const eased =
          1 -
          Math.pow(
            1 - ratio,
            3
          );

        const pct =
          Math.min(
            100,
            Math.round(
              eased * 100
            )
          );

        if (number) {
          number.textContent =
            `${pct}%`;
        }

        if (bar) {
          bar.style.width =
            `${pct}%`;
        }

        if (
          message &&
          messages.length
        ) {
          if (pct < 38) {
            message.textContent =
              messages[0] ||
              '';
          } else if (pct < 82) {
            message.textContent =
              messages[1] ||
              messages[0] ||
              '';
          } else {
            message.textContent =
              messages[2] ||
              messages[
                messages.length - 1
              ] ||
              '';
          }
        }

        if (ratio < 1) {
          requestAnimationFrame(
            frame
          );

          return;
        }

        window.setTimeout(
          completeQuiz,
          120
        );
      }

      requestAnimationFrame(frame);
    }

    function completeQuiz() {
      state.phase = 'result';

      state.resultId =
        selectResult();

      state.completedAt =
        new Date().toISOString();

      emit('QuizComplete', {
        result_id:
          state.resultId,

        scores:
          calculateScores()
      });

      sendQuizCompletionData();

      renderResult();
    }

    function sendQuizCompletionData() {
      if (
        completionDataSent ||
        !config.dataWebhookUrl
      ) {
        return;
      }

      completionDataSent = true;

      const params =
        new URLSearchParams(
          window.location.search
        );

      const payload = {
        quiz_session_id:
          quizSessionId,

        quiz_id:
          config.quizId,

        quiz_version:
          config.version,

        started_at:
          state.startedAt,

        completed_at:
          state.completedAt,

        result_id:
          state.resultId,

        main_objection:
          state.answers
            .q09_objections ||
          null,

        emotional_intention:
          state.answerLabels
            .q10_meaning ||
          null,

        answers:
          state.answers,

        answer_labels:
          state.answerLabels,

        page_url:
          window.location.href,

        referrer:
          document.referrer ||
          null,

        utm_source:
          params.get(
            'utm_source'
          ),

        utm_medium:
          params.get(
            'utm_medium'
          ),

        utm_campaign:
          params.get(
            'utm_campaign'
          ),

        utm_content:
          params.get(
            'utm_content'
          ),

        utm_term:
          params.get(
            'utm_term'
          ),

        fbclid:
          params.get(
            'fbclid'
          ),

        gclid:
          params.get(
            'gclid'
          ),

        ttclid:
          params.get(
            'ttclid'
          )
      };

      const body =
        new URLSearchParams();

      body.set(
        'payload',
        JSON.stringify(payload)
      );

      if (
        typeof navigator.sendBeacon ===
        'function'
      ) {
        try {
          const sent =
            navigator.sendBeacon(
              config.dataWebhookUrl,
              body
            );

          if (sent) {
            return;
          }
        } catch (_) {}
      }

      fetch(
        config.dataWebhookUrl,
        {
          method: 'POST',
          body,
          mode: 'no-cors',
          keepalive: true
        }
      ).catch(() => {});
    }

    function renderResult() {
      const resultId =
        state.resultId ||
        selectResult();

      const result =
        config.results[
          resultId
        ] ||
        config.results
          .playful_direct;

      const meaningInterpretation =
        getMeaningInterpretation(
          state.answers
            .q10_meaning
        );

      const mainObjection =
        state.answers
          .q09_objections ||
        'none';

      const productFit =
        getProductFitState();

      const useAlternativeCollection =
        productFit.isMismatch &&
        Boolean(
          config.alternativeCollection?.url
        );

      const destination =
        getDestinationPresentation(
          productFit,
          useAlternativeCollection
        );

      const destinationHref =
        buildDestinationHref(
          destination.url,
          resultId
        );

      let resultImage;

      if (useAlternativeCollection) {
        resultImage =
          config.alternativeCollection
            ?.image ||
          null;
      } else {
        resultImage =
          getDynamicResultImage(
            mainObjection
          );
      }

      const reassurance =
        useAlternativeCollection
          ? ''
          : getReassuranceLine(
              mainObjection
            );

      app.innerHTML = `
        <div class="iflq-shell">

          <section
            class="iflq-result-card iflq-result-page"
          >

            <div
              class="iflq-result-summary"
            >

              <p class="iflq-kicker">
                ${escapeHtml(
                  result.eyebrow
                )}
              </p>

              <h1
                class="iflq-title"
              >
                ${escapeHtml(
                  result.headline
                )}
              </h1>

              <p class="iflq-copy">
                ${escapeHtml(
                  result.body
                )}
              </p>

              ${
                meaningInterpretation
                  ? `
                    <div
                      class="iflq-reflection"
                    >

                      <small>
                        Ce qui ressort surtout de tes réponses :
                      </small>

                      <strong>
                        ${escapeHtml(
                          meaningInterpretation
                        )}
                      </strong>

                    </div>
                  `
                  : ''
              }

            </div>

            <div
              class="iflq-result-separator"
              aria-hidden="true"
            ></div>

            <div
              class="iflq-recommendation-wrap"
            >

              <div
                class="iflq-recommendation-layout"
              >

                <div
                  class="iflq-recommendation-copy"
                >

                  <p class="iflq-kicker">
                    ${escapeHtml(
                      destination.heading
                    )}
                  </p>

                  <h2
                    class="iflq-product-title"
                  >
                    ${escapeHtml(
                      destination.title
                    )}
                  </h2>

                  <p class="iflq-copy">
                    ${escapeHtml(
                      destination.body
                    )}
                  </p>

                  ${
                    reassurance
                      ? `
                        <ul
                          class="iflq-reassurance-list"
                        >
                          <li>

                            <span
                              class="iflq-check"
                              aria-hidden="true"
                            >
                              ✓
                            </span>

                            <span>
                              ${escapeHtml(
                                reassurance
                              )}
                            </span>

                          </li>
                        </ul>
                      `
                      : ''
                  }

                  <div
                    class="iflq-final-cta"
                  >

                    <a
                      class="iflq-btn"
                      href="${escapeHtml(
                        destinationHref
                      )}"
                      data-action="pdp"
                    >
                      ${escapeHtml(
                        destination.cta
                      )}
                    </a>

                  </div>

                </div>

                ${
                  resultImage
                    ? `
                      <div
                        class="iflq-recommendation-image"
                      >

                        <img
                          src="${escapeHtml(
                            resultImage
                          )}"
                          alt="${escapeHtml(
                            destination.title
                          )}"
                          loading="lazy"
                        >

                      </div>
                    `
                    : ''
                }

              </div>

              ${
                useAlternativeCollection
                  ? ''
                  : reviewHtml(
                      config.review
                    )
              }

            </div>

          </section>

        </div>
      `;

      emit('QuizResult', {
        result_id:
          resultId,

        scores:
          calculateScores(),

        main_objection:
          mainObjection,

        destination_type:
          useAlternativeCollection
            ? 'collection'
            : 'product'
      });

      app
        .querySelector(
          '[data-action="pdp"]'
        )
        ?.addEventListener(
          'click',
          () => {
            emit(
              'QuizPDPClick',
              {
                result_id:
                  resultId,

                main_objection:
                  mainObjection,

                destination_type:
                  useAlternativeCollection
                    ? 'collection'
                    : 'product'
              }
            );
          }
        );
    }

    function getProductFitState() {
      const toneMismatch =
        state.answers
          .q07_blunt ===
        'not_her';

      const ringMismatch =
        state.answers
          .q08_rings ===
        'never';

      return {
        toneMismatch,
        ringMismatch,
        isMismatch:
          toneMismatch ||
          ringMismatch
      };
    }

    function getDestinationPresentation(
      productFit,
      useAlternativeCollection
    ) {
      if (!useAlternativeCollection) {
        return {
          heading:
            config
              .recommendation
              .heading,

          title:
            config
              .recommendation
              .productTitle,

          body:
            config
              .recommendation
              .body,

          cta:
            config
              .recommendation
              .cta,

          url:
            config.productUrl
        };
      }

      const collectionTitle =
        config.alternativeCollection
          ?.title ||
        'Cadeaux pour elle';

      if (
        productFit.toneMismatch &&
        productFit.ringMismatch
      ) {
        return {
          heading:
            'ON A QUELQUE CHOSE QUI DEVRAIT MIEUX LUI RESSEMBLER',

          title:
            collectionTitle,

          body:
            'Entre le ton très cru et le fait qu’elle ne porte jamais de bagues, cette version ne semble pas être le meilleur match. Voici plutôt nos cadeaux pour elle dans des styles plus doux et dans d’autres formats, tout en gardant le côté personnel.',

          cta:
            'Voir les cadeaux pour elle →',

          url:
            config
              .alternativeCollection
              .url
        };
      }

      if (
        productFit.toneMismatch
      ) {
        return {
          heading:
            'ON A QUELQUE CHOSE QUI DEVRAIT MIEUX LUI RESSEMBLER',

          title:
            collectionTitle,

          body:
            'Tu nous as dit que le langage très cru n’est pas vraiment son style. On te montre plutôt des cadeaux pour elle avec un ton plus doux, mais toujours personnel.',

          cta:
            'Voir les cadeaux pour elle →',

          url:
            config
              .alternativeCollection
              .url
        };
      }

      return {
        heading:
          'ON A QUELQUE CHOSE QUI DEVRAIT MIEUX LUI RESSEMBLER',

        title:
          collectionTitle,

        body:
          'Tu nous as dit qu’elle ne porte jamais de bagues. On te montre plutôt des cadeaux pour elle dans d’autres formats, sans perdre le côté personnel.',

        cta:
          'Voir les cadeaux pour elle →',

        url:
          config
            .alternativeCollection
            .url
      };
    }

    function getMeaningInterpretation(
      answerId
    ) {
      const interpretations = {
        still_crazy:
          'Tu veux qu’elle sente qu’après tout ce temps, il y a encore du feu — pas juste de l’habitude.',

        us:
          'Tu veux lui rappeler que votre couple compte encore dans le quotidien, même sans grande occasion.',

        more_than_say:
          'Tu veux que le cadeau dise à ta place ce que tu ne verbalises pas toujours.',

        my_person:
          'Tu veux lui rappeler qu’après tout ce temps, c’est encore elle que tu choisis.'
      };

      return (
        interpretations[
          answerId
        ] || ''
      );
    }

    function getDynamicResultImage(
      mainObjection
    ) {
      if (
        mainObjection ===
          'visible' &&
        config.images.worn
      ) {
        return config.images.worn;
      }

      if (
        mainObjection ===
          'quality' &&
        config.images.quality
      ) {
        return config.images.quality;
      }

      if (
        mainObjection ===
          'size' &&
        config.images.size
      ) {
        return config.images.size;
      }

      if (
        mainObjection ===
          'delivery' &&
        config.images.delivery
      ) {
        return config.images.delivery;
      }

      return (
        config.images
          .defaultResult ||
        null
      );
    }

    function getReassuranceLine(
      mainObjection
    ) {
      if (
        mainObjection ===
        'size'
      ) {
        return (
          config
            .reassuranceBullets
            ?.size || ''
        );
      }

      if (
        mainObjection ===
        'quality'
      ) {
        return (
          config
            .reassuranceBullets
            ?.quality || ''
        );
      }

      if (
        mainObjection ===
        'delivery'
      ) {
        return (
          config
            .reassuranceBullets
            ?.delivery || ''
        );
      }

      return '';
    }

    function reviewHtml(
      review
    ) {
      if (
        !review ||
        (
          !review.quote &&
          !review.image
        )
      ) {
        return '';
      }

      return `
        <div
          class="iflq-review iflq-review--inline"
        >

          <div
            class="iflq-review-grid"
          >

            ${
              review.image
                ? `
                  <img
                    class="iflq-review__image"
                    src="${escapeHtml(
                      review.image
                    )}"
                    alt=""
                    loading="lazy"
                  >
                `
                : ''
            }

            <div>

              ${
                review.showStars
                  ? `
                    <div
                      class="iflq-stars"
                      aria-label="5 étoiles"
                    >
                      ★★★★★
                    </div>
                  `
                  : ''
              }

              ${
                review.quote
                  ? `
                    <blockquote>
                      “${escapeHtml(
                        review.quote
                      )}”
                    </blockquote>
                  `
                  : ''
              }

              ${
                review.author
                  ? `
                    <cite>
                      ${escapeHtml(
                        review.author
                      )}
                    </cite>
                  `
                  : ''
              }

            </div>

          </div>

        </div>
      `;
    }

    function calculateScores(
      upToScreenId = null
    ) {
      const totals = {};

      for (
        const screen of
        config.screens
      ) {
        const selected =
          state.answers[
            screen.id
          ];

        if (selected != null) {
          if (
            screen.kind ===
            'single'
          ) {
            const answer =
              screen.answers?.find(
                (item) =>
                  item.id ===
                  selected
              );

            addScores(
              totals,
              answer?.scores
            );
          }

          if (
            screen.kind ===
            'multi'
          ) {
            for (
              const answerId of
              normalizeArray(
                selected
              )
            ) {
              const answer =
                screen.answers?.find(
                  (item) =>
                    item.id ===
                    answerId
                );

              addScores(
                totals,
                answer?.scores
              );
            }
          }

          if (
            screen.kind ===
            'slider'
          ) {
            addScores(
              totals,
              screen.scoreMap?.[
                String(
                  selected
                )
              ]
            );
          }
        }

        if (
          upToScreenId &&
          screen.id ===
            upToScreenId
        ) {
          break;
        }
      }

      return totals;
    }

    function addScores(
      totals,
      scores
    ) {
      if (!scores) {
        return;
      }

      for (
        const [
          dimension,
          value
        ] of Object.entries(
          scores
        )
      ) {
        totals[
          dimension
        ] =
          (
            totals[
              dimension
            ] || 0
          ) +
          Number(
            value || 0
          );
      }
    }

    function getMidCardObservation() {
      const scores =
        calculateScores(
          'q05_reaction'
        );

      for (
        const rule of
        config
          .midCardRules ||
        []
      ) {
        if (
          evaluateCondition(
            rule.when,
            scores
          )
        ) {
          return rule.message;
        }
      }

      return config.midCardFallback;
    }

    function selectResult() {
      const scores =
        calculateScores();

      for (
        const rule of
        config.resultRules
      ) {
        if (rule.default) {
          return rule.resultId;
        }

        if (
          evaluateCondition(
            rule.when,
            scores
          )
        ) {
          return rule.resultId;
        }
      }

      return Object.keys(
        config.results
      )[0];
    }

    function evaluateCondition(
      condition,
      scores
    ) {
      if (!condition) {
        return false;
      }

      if (
        Array.isArray(
          condition.all
        )
      ) {
        return condition.all.every(
          (item) =>
            evaluateCondition(
              item,
              scores
            )
        );
      }

      if (
        Array.isArray(
          condition.any
        )
      ) {
        return condition.any.some(
          (item) =>
            evaluateCondition(
              item,
              scores
            )
        );
      }

      if (condition.score) {
        const {
          dimension,
          gte,
          gt,
          lte,
          lt,
          eq
        } = condition.score;

        const value =
          Number(
            scores[
              dimension
            ] || 0
          );

        if (
          gte != null &&
          !(
            value >=
            Number(gte)
          )
        ) {
          return false;
        }

        if (
          gt != null &&
          !(
            value >
            Number(gt)
          )
        ) {
          return false;
        }

        if (
          lte != null &&
          !(
            value <=
            Number(lte)
          )
        ) {
          return false;
        }

        if (
          lt != null &&
          !(
            value <
            Number(lt)
          )
        ) {
          return false;
        }

        if (
          eq != null &&
          !(
            value ===
            Number(eq)
          )
        ) {
          return false;
        }

        return true;
      }

      if (
        condition
          .answerEquals
      ) {
        const {
          questionId,
          value
        } =
          condition.answerEquals;

        const selected =
          state.answers[
            questionId
          ];

        return Array.isArray(
          selected
        )
          ? selected.includes(
              value
            )
          : selected ===
            value;
      }

      return false;
    }

    function countCompletedQuestionsBefore(
      screenIndex
    ) {
      let count = 0;

      for (
        let i = 0;
        i < screenIndex;
        i += 1
      ) {
        const screen =
          config.screens[i];

        if (
          [
            'single',
            'multi',
            'slider'
          ].includes(
            screen.kind
          ) &&
          state.answers[
            screen.id
          ] != null
        ) {
          count += 1;
        }
      }

      return count;
    }

    function normalizeArray(
      value
    ) {
      if (
        Array.isArray(value)
      ) {
        return value;
      }

      if (value == null) {
        return [];
      }

      return [value];
    }

    function getAnswerLabel(
      screen,
      answerId
    ) {
      return (
        screen.answers?.find(
          (item) =>
            item.id ===
            answerId
        )?.label ||
        answerId
      );
    }

    function buildDestinationHref(
      baseUrl,
      resultId
    ) {
      let target;

      try {
        target =
          new URL(
            baseUrl || '/',
            window.location.origin
          );
      } catch (_) {
        target =
          new URL(
            '/',
            window.location.origin
          );
      }

      const current =
        new URL(
          window.location.href
        );

      for (
        const [
          key,
          value
        ] of current
          .searchParams
          .entries()
      ) {
        if (
          TRACKING_PARAMS.has(
            key
          ) ||
          key.startsWith(
            'utm_'
          )
        ) {
          target.searchParams.set(
            key,
            value
          );
        }
      }

      target.searchParams.set(
        'quiz_id',
        config.quizId
      );

      target.searchParams.set(
        'quiz_version',
        config.version
      );

      target.searchParams.set(
        'quiz_result',
        resultId
      );

      target.searchParams.set(
        'quiz_session_id',
        quizSessionId
      );

      return `${target.pathname}${target.search}${target.hash}`;
    }
  }

  function bootWithin(
    container = document
  ) {
    container
      .querySelectorAll(
        ROOT_SELECTOR
      )
      .forEach(initQuiz);

    if (
      container.matches?.(
        ROOT_SELECTOR
      )
    ) {
      initQuiz(container);
    }
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () =>
        bootWithin(document),
      {
        once: true
      }
    );
  } else {
    bootWithin(document);
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      bootWithin(
        event.target
      );
    }
  );
})();